/**
 * WCO-2322 (Phase 6A) — multi-destination Transfer-Back picker for the Add/Edit Production Session modal.
 * Presentation only: collects { code, cases } rows + a UX default split. ALL allocation/business logic
 * (output-number minting/reuse, tombstoning, dedupe/validation) lives in the backend
 * (syncSessionOutputs / normalizeTransferBackDestinations). The parent sends `transfer_back_destinations`.
 *
 * Behavior (per César):
 *  - Default case split = Target Cases distributed evenly, remainder to the FIRST location(s) (5/2 → 3,2;
 *    5/3 → 2,2,1). The auto-split fires when a destination is added/removed or Target Cases changes, but
 *    ONLY while the split is not "authoritative". A split is authoritative once it is prefilled from a
 *    saved session (WCO-2419) or manually edited — then it is preserved (not reset to the even split) and
 *    persists on save. A manual edit to one row never changes the others.
 *  - Adding a destination hides locations already chosen in other rows.
 *  - Cases-input border: yellow when the total is UNDER Target Cases, red when OVER, default when exact.
 */
import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { Plus, Trash2 } from 'lucide-react';

export interface DestinationRow {
  code: string;
  cases: string; // kept as string for controlled <input type="number">
}

interface LocationOption {
  location_code?: string;
  description?: string;
}

interface TransferBackDestinationsProps {
  destinations: DestinationRow[];
  onChange: (rows: DestinationRow[]) => void;
  targetCases: number;
  locationCodes: LocationOption[];
}

// Even split with the remainder added to the FIRST location(s): split(5,2) → [3,2]; split(5,3) → [2,2,1].
function splitCases(target: number, n: number): number[] {
  if (n <= 0) return [];
  const safeTarget = Math.max(0, target);
  const base = Math.floor(safeTarget / n);
  const remainder = safeTarget % n;
  return Array.from({ length: n }, (_, i) => base + (i < remainder ? 1 : 0));
}

const TransferBackDestinations: React.FC<TransferBackDestinationsProps> = ({
  destinations,
  onChange,
  targetCases,
  locationCodes,
}) => {
  const { t } = useLanguage();

  // WCO-2419: case values are "authoritative" (must NOT be auto-overwritten) when they were prefilled
  // from a saved session or manually edited by the user. Starts true if the picker mounts already holding
  // any non-empty cases (edit-prefill); false for the blank create default. Add/remove resets it (a fresh
  // structural change re-distributes evenly); a manual cases edit sets it. This lets a stored/edited split
  // (e.g. 3/7) survive on display and on save instead of being reset to the even split.
  const casesAreAuthoritative = useRef(destinations.some((d) => d.cases !== ''));

  // Auto-split default: distribute Target Cases evenly (remainder to the first locations). Re-applies when
  // a destination is added/removed OR Target Cases changes — but ONLY while the case values are not
  // authoritative, so prefilled/manual splits are preserved.
  //
  // WCO-2520: the authoritative guard is bypassed when there is exactly ONE destination. A lone
  // destination has no custom split to preserve — its cases are always the whole Target Cases — so it
  // must track Target Cases even in edit mode (where the split mounts authoritative from prefill). Without
  // this, editing Target Cases left the single destination stale (e.g. 60 vs a new 50), tripping the
  // over/under validation and disabling Save. Multi-destination keeps the WCO-2419 authoritative behavior.
  useEffect(() => {
    if (destinations.length === 0) return;
    if (casesAreAuthoritative.current && destinations.length > 1) return;
    const split = splitCases(targetCases, destinations.length);
    const next = destinations.map((d, i) => ({ ...d, cases: String(split[i]) }));
    if (next.some((d, i) => d.cases !== destinations[i].cases)) {
      onChange(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetCases, destinations.length]);

  const sumCases = destinations.reduce((acc, r) => acc + (parseInt(r.cases) || 0), 0);
  const selectedCodes = destinations.map((r) => r.code).filter(Boolean);
  const hasDuplicate = new Set(selectedCodes).size !== selectedCodes.length;
  const allLocationsUsed = selectedCodes.length >= locationCodes.length;

  // Border feedback on the cases inputs: under target → yellow, over → red, exact → default (no green).
  const casesBorder =
    sumCases < targetCases
      ? 'border-yellow-400 dark:border-yellow-500'
      : sumCases > targetCases
        ? 'border-red-500 dark:border-red-500'
        : 'border-gray-300 dark:border-gray-600';

  // Add/remove change the row count; clear the authoritative flag so the effect re-applies the even split.
  const addRow = () => { casesAreAuthoritative.current = false; onChange([...destinations, { code: '', cases: '' }]); };
  const removeRow = (index: number) => { casesAreAuthoritative.current = false; onChange(destinations.filter((_, i) => i !== index)); };
  // Manual edits never re-split the others; a cases edit marks the split authoritative so it is preserved.
  const updateRow = (index: number, patch: Partial<DestinationRow>) => {
    if ('cases' in patch) casesAreAuthoritative.current = true;
    onChange(destinations.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  // Options for a row exclude codes already chosen in OTHER rows (keep this row's own current pick).
  const optionsFor = (rowCode: string) => {
    const usedElsewhere = new Set(destinations.map((d) => d.code).filter((c) => c && c !== rowCode));
    return locationCodes.filter((loc) => !usedElsewhere.has(loc.location_code || ''));
  };

  // Match the Source Location field sizing (text-sm, px-2 py-1.5, rounded-lg).
  const fieldCls =
    'px-2 py-1.5 text-sm border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500';

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label
          className="block text-xs font-medium text-gray-700 dark:text-gray-300 cursor-help"
          title={t('supervisor.transferBackDestinationsHint')}
        >
          {t('supervisor.transferBackDestinations')} *
        </label>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {sumCases} / {targetCases} {t('supervisor.casesUnit')}
        </span>
      </div>

      <div className="space-y-1.5">
        {destinations.map((row, index) => (
          <div key={index} className="flex items-center gap-1.5">
            <select
              value={row.code}
              onChange={(e) => updateRow(index, { code: e.target.value })}
              className={`flex-1 ${fieldCls} border-gray-300 dark:border-gray-600`}
            >
              <option value="">{t('supervisor.selectDestination')}</option>
              {optionsFor(row.code).map((loc) => (
                <option key={loc.location_code} value={loc.location_code}>
                  {loc.location_code}{loc.description ? ` - ${loc.description}` : ''}
                </option>
              ))}
            </select>
            <input
              type="number"
              min={0}
              value={row.cases}
              onChange={(e) => updateRow(index, { cases: e.target.value })}
              className={`w-16 ${fieldCls} ${casesBorder}`}
            />
            <button
              type="button"
              onClick={() => removeRow(index)}
              disabled={destinations.length <= 1}
              className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addRow}
        disabled={allLocationsUsed}
        className="mt-1.5 flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-300 hover:text-indigo-700 dark:hover:text-indigo-200 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Plus className="w-3.5 h-3.5" /> {t('supervisor.addDestination')}
      </button>

      {hasDuplicate && (
        <div className="mt-1 text-xs text-red-500 dark:text-red-300">
          {t('supervisor.duplicateDestination')}
        </div>
      )}
    </div>
  );
};

export default TransferBackDestinations;
